import React from "react";
import { tokens } from "../../../public/tokens";

const { colors, typography, globals } = tokens;

/**
 * Shared text/number input.
 *
 * @param {Object} props
 * @param {string} [props.type="text"] - Input type.
 * @param {string} [props.error] - Validation message.
 * @param {boolean} [props.touched] - Whether the field has been touched.
 * @param {string} [props.prefix] - Unit or currency adornment (e.g. "R") rendered
 *   inside the field's own border, divided from the value by a hairline.
 * @returns {JSX.Element}
 */
const FormInput = ({
  type = "text",
  error,
  touched,
  style = {},
  className = "",
  prefix,
  ...props
}) => {
  const hasError = error && touched;
  const fieldClasses = ["aie-form-input", hasError ? "aie-form-input--error" : "", className]
    .filter(Boolean)
    .join(" ");
  const fieldStyle = {
    minHeight: "40px",
    borderRadius: globals["border-radius"],
    fontSize: typography.fontSize["lg-14px"],
    fontFamily: globals["font-family"],
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    width: "100%",
    ...style,
  };

  const errorMessage = hasError ? (
    <div
      className="invalid-feedback d-block"
      style={{ fontSize: typography.fontSize["md-12px"], color: colors.states.alert.default }}
    >
      {error}
    </div>
  ) : null;

  if (prefix) {
    return (
      <>
        <div
          className={fieldClasses}
          style={{
            ...fieldStyle,
            display: "flex",
            alignItems: "stretch",
            padding: 0,
            overflow: "hidden",
            backgroundColor: colors.neutral.white,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              padding: `0 ${globals["spacing-3"]}`,
              color: colors.text.primary,
            }}
          >
            {prefix}
          </span>
          <span
            aria-hidden="true"
            style={{
              alignSelf: "stretch",
              flexShrink: 0,
              width: "1px",
              margin: `${globals["spacing-2"]} 0`,
              backgroundColor: colors.surfaces["border-divider"],
            }}
          />
          <input
            type={type}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
              fontSize: typography.fontSize["lg-14px"],
              fontFamily: globals["font-family"],
              color: colors.text.primary,
            }}
            {...props}
          />
        </div>
        {errorMessage}
      </>
    );
  }

  return (
    <>
      <input type={type} className={fieldClasses} style={fieldStyle} {...props} />
      {errorMessage}
    </>
  );
};

export default FormInput;
